/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './AuthContext';
import { parseConfigValue } from '../utils/integrationConfig';

// Fallback imports — used when Supabase is unavailable
import { users as fallbackUsers, getUserById as fallbackGetUserById, currentUserId } from '../data/users';
import { teams as fallbackTeams } from '../data/teams';

const DataContext = createContext();

/**
 * DataProvider wraps the app and provides all core entity data from Supabase.
 * Falls back to static mock data if Supabase connection fails.
 *
 * This is the bridge between the prototype (static data) and production (live database).
 * Pages should import from useData() instead of directly from data files.
 */
export function DataProvider({ children }) {
  const { user: authUser, loading: authLoading, effectiveUserId, effectiveOrgId, effectiveRole } = useAuth();
  const activeUserId = effectiveUserId || authUser?.id || null;
  // Phase 11 slot 8 (2026-04-24): drop user_metadata fallback per migration 055.
  const activeOrgId = effectiveOrgId || authUser?.app_metadata?.org_id;
  const canLoadAnnotatorGraph = !!authUser && effectiveRole === 'annotator';
  const canLoadSharedOrgData = !!authUser && ['annotator', 'admin', 'spl', 'god'].includes(effectiveRole);
  const canLoadOrganization = !!authUser && !!activeOrgId;
  const [users, setUsers] = useState([]);
  const [teams, setTeams] = useState({});
  const [currentUser, setCurrentUser] = useState(null);
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [dataSource, setDataSource] = useState('loading');
  const [stepLibrary, setStepLibrary] = useState([]);
  const [onboardingTemplate, setOnboardingTemplate] = useState([]);
  const [organization, setOrganization] = useState(null); // 'supabase' | 'fallback' | 'loading'

  const loadCoreData = useCallback(async () => {
    if (authLoading) return;

    if (!authUser) {
      setUsers([]);
      setTeams({});
      setCurrentUser(null);
      setConfig({});
      setStepLibrary([]);
      setOnboardingTemplate([]);
      setOrganization(null);
      setDataSource('idle');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);

      let annotatorSeedUser = null;
      let annotatorTeamId = null;

      if (canLoadAnnotatorGraph && activeUserId) {
        let seedUserQuery = supabase.from('individual').select('*').eq('id', activeUserId);
        if (activeOrgId) {
          seedUserQuery = seedUserQuery.eq('org_id', activeOrgId);
        }
        const { data: seedUser, error: seedUserError } = await seedUserQuery.single();
        if (seedUserError) throw seedUserError;
        annotatorSeedUser = seedUser;
        annotatorTeamId = seedUser?.current_team_id || null;
      }

      let usersQuery = supabase.from('individual').select('*').order('craft_score_overall', { ascending: false });
      let teamsQuery = supabase.from('team').select('*').eq('status', 'Active');
      let configQuery = supabase.from('system_config').select('*');
      let templateQuery = supabase.from('onboarding_template').select('*, step:step_id(*)').order('sort_order');
      let shouldFetchTeams = canLoadAnnotatorGraph && !!activeOrgId;

      if (activeOrgId) {
        if (canLoadAnnotatorGraph) {
          if (annotatorTeamId) {
            usersQuery = usersQuery
              .eq('org_id', activeOrgId)
              .or(`id.eq.${activeUserId},current_team_id.eq.${annotatorTeamId}`);
            teamsQuery = teamsQuery.eq('org_id', activeOrgId).eq('id', annotatorTeamId);
          } else {
            usersQuery = usersQuery.eq('id', activeUserId).eq('org_id', activeOrgId);
            shouldFetchTeams = false;
          }
        }
        if (canLoadSharedOrgData) {
          configQuery = configQuery.or(`org_id.is.null,org_id.eq.${activeOrgId}`);
          templateQuery = templateQuery.eq('org_id', activeOrgId);
        }
      } else if (canLoadAnnotatorGraph && activeUserId) {
        usersQuery = usersQuery.eq('id', activeUserId);
      }

      // Fetch core entities + onboarding data in parallel
      const [usersRes, teamsRes, configRes, stepLibraryRes, templateRes, orgRes] = await Promise.all([
        canLoadAnnotatorGraph ? usersQuery : Promise.resolve({ data: [], error: null }),
        shouldFetchTeams
          ? teamsQuery
          : Promise.resolve({ data: [], error: null }),
        canLoadSharedOrgData && activeOrgId ? configQuery : Promise.resolve({ data: [], error: null }),
        canLoadSharedOrgData ? supabase.from('onboarding_step_library').select('*').order('sort_order') : Promise.resolve({ data: [], error: null }),
        canLoadSharedOrgData && activeOrgId ? templateQuery : Promise.resolve({ data: [], error: null }),
        canLoadOrganization
          ? supabase.from('organization').select('*').eq('id', activeOrgId).single()
          : Promise.resolve({ data: null, error: null }),
      ]);

      if (usersRes.error) throw usersRes.error;
      if (teamsRes.error) throw teamsRes.error;
      if (configRes.error) throw configRes.error;
      // Non-critical: step library and template may fail gracefully
      if (stepLibraryRes.data) setStepLibrary(stepLibraryRes.data);
      if (templateRes.data) setOnboardingTemplate(templateRes.data);
      if (orgRes.data) setOrganization(orgRes.data);

      // Transform users to match frontend data structure
      const transformedUsers = (usersRes.data || []).map(u => ({
        // Keep all database fields
        ...u,
        // Map to frontend field names for backwards compatibility
        id: u.id,
        name: u.name,
        email: u.email,
        domain: u.primary_domain,
        badge: u.badge,
        quality: u.quality_score,
        projects: u.total_jobs_completed,
        initials: u.name.split(' ').map(n => n[0]).join(''),
        streak: u.streak,
        bio: u.bio,
        linkedin: u.linkedin_url,
        website: u.website,
        availability: u.status === 'Active' ? 'Active' : u.status,
        craftScore: {
          overall: u.craft_score_overall,
          precision: u.craft_score_precision,
          consistency: u.craft_score_consistency,
          edgeCases: u.craft_score_edge_cases,
          knowledgeSharing: u.craft_score_knowledge,
        },
        edgeCasesFlagged: u.edge_cases_flagged,
        insightsShared: u.insights_shared,
        daysActiveThisCycle: u.days_active_this_cycle,
        currentTeam: u.current_team_id ? {
          id: u.current_team_id,
          // Team name will be resolved after teams load
        } : null,
        // Database UUID — keep for queries
        _dbId: u.id,
      }));

      // Transform teams to match frontend data structure (keyed by team slug)
      const transformedTeams = {};
      (teamsRes.data || []).forEach(t => {
        // Derive slug from team name
        const slug = t.name.replace('Team ', '').toLowerCase();
        transformedTeams[slug] = {
          ...t,
          id: slug,
          _dbId: t.id,
          name: t.name,
          domain: t.domain,
          subDomain: t.sub_domain,
          craftScore: t.craft_score,
          sprintsCleared: t.sprints_cleared,
          totalAnnotations: t.total_annotations,
          averageQuality: t.average_quality,
          members: transformedUsers.filter(u => u.current_team_id === t.id).map(u => ({
            id: u.id,
            name: u.name,
            domain: u.primary_domain || u.domain,
            badge: u.badge,
            initials: u.initials,
            craftScore: u.craftScore,
            streak: u.streak,
          })),
        };
      });

      // Resolve team names on users
      transformedUsers.forEach(u => {
        if (u.current_team_id) {
          const team = (teamsRes.data || []).find(t => t.id === u.current_team_id);
          if (team) {
            const slug = team.name.replace('Team ', '').toLowerCase();
            u.currentTeam = { name: team.name, id: slug, _dbId: team.id };
          }
        }
      });

      // Transform config into key-value map
      const configMap = {};
      const orderedConfigRows = [...(configRes.data || [])].sort((a, b) => {
        if (a.org_id === b.org_id) return 0;
        if (a.org_id == null) return -1;
        if (b.org_id == null) return 1;
        return 0;
      });
      orderedConfigRows.forEach(c => {
        configMap[c.key] = parseConfigValue(c.value);
      });

      const current = canLoadAnnotatorGraph
        ? transformedUsers.find(u => u.id === activeUserId) || (
          annotatorSeedUser
            ? {
                ...annotatorSeedUser,
                domain: annotatorSeedUser.primary_domain,
                quality: annotatorSeedUser.quality_score,
                projects: annotatorSeedUser.total_jobs_completed,
                initials: annotatorSeedUser.name.split(' ').map(n => n[0]).join(''),
                streak: annotatorSeedUser.streak,
                linkedin: annotatorSeedUser.linkedin_url,
                availability: annotatorSeedUser.status === 'Active' ? 'Active' : annotatorSeedUser.status,
                craftScore: {
                  overall: annotatorSeedUser.craft_score_overall,
                  precision: annotatorSeedUser.craft_score_precision,
                  consistency: annotatorSeedUser.craft_score_consistency,
                  edgeCases: annotatorSeedUser.craft_score_edge_cases,
                  knowledgeSharing: annotatorSeedUser.craft_score_knowledge,
                },
                edgeCasesFlagged: annotatorSeedUser.edge_cases_flagged,
                insightsShared: annotatorSeedUser.insights_shared,
                daysActiveThisCycle: annotatorSeedUser.days_active_this_cycle,
                _dbId: annotatorSeedUser.id,
              }
            : null
        )
        : null;

      setUsers(transformedUsers);
      setTeams(transformedTeams);
      setCurrentUser(current);
      setConfig(configMap);
      setDataSource('supabase');
      if (import.meta.env.DEV) console.log(`✅ Supabase connected — ${transformedUsers.length} users, ${Object.keys(transformedTeams).length} teams loaded`);

    } catch (err) {
      console.error('❌ Supabase connection failed:', err.message);

      // Only fall back to static data in development — never silently in production.
      // In prod, log loudly to Sentry if this branch is ever reached so we catch
      // any accidental fallback firing (Bug 32 defense-in-depth).
      const isDev = import.meta.env.DEV;
      if (isDev && canLoadAnnotatorGraph) {
        console.warn('⚠️ DEV MODE: Using static fallback data. This would be an error in production.');
        setUsers(fallbackUsers);
        setTeams(fallbackTeams);
        setCurrentUser(fallbackUsers.find(u => u.id === currentUserId));
        setConfig({
          'streak.badge_min': 4,
          'streak.gold_threshold': 8,
          'craft_score.sprint_threshold': 93.0,
          'rewards.cash_bonus': 150,
          'rewards.rate_boost_percent': 10,
          'rewards.rate_boost_hours': 6,
          'rewards.facilitator_bonus': 75,
        });
        setDataSource('fallback');
      } else {
        // Production: show error state, don't fake it
        if (!isDev) {
          // Report to Sentry so we see if Supabase outages are actually hitting prod
          import('../lib/sentry').then(m => {
            m.captureError?.(err, { location: 'DataProvider.loadCoreData', prod_fallback_attempted: true });
          }).catch(() => {});
        }
        setUsers([]);
        setTeams({});
        setCurrentUser(null);
        setConfig({});
        setStepLibrary([]);
        setOnboardingTemplate([]);
        setOrganization(null);
        setDataSource('error');
      }
    } finally {
      setLoading(false);
    }
  }, [activeOrgId, activeUserId, authLoading, authUser, canLoadAnnotatorGraph, canLoadOrganization, canLoadSharedOrgData]);

  useEffect(() => {
    if (authLoading) {
      setLoading(true);
      return;
    }
    loadCoreData();
  }, [authLoading, loadCoreData]);

  // Helper functions
  const getUserById = (id) => {
    if (dataSource === 'fallback') return fallbackGetUserById(id);
    const numId = Number(id);
    return users.find(u =>
      u.id === id ||
      u._dbId === id ||
      (numId && u.id.endsWith(String(numId).padStart(12, '0')))
    );
  };

  const getTeamById = (id) => {
    if (dataSource === 'fallback') return fallbackTeams[id];
    return teams[id] || Object.values(teams).find(t => t._dbId === id);
  };

  const getConfig = (key, defaultValue) => {
    return config[key] ?? defaultValue;
  };

  return (
    <DataContext.Provider value={{
      // Core data
      users,
      teams,
      currentUser,
      config,

      // Metadata
      loading,
      dataSource,

      // Helpers
      getUserById,
      getTeamById,
      getConfig,
      refreshCoreData: loadCoreData,

      // Constants (always available regardless of data source)
      currentUserId: dataSource === 'fallback' ? currentUserId : activeUserId,
      orgId: activeOrgId || currentUser?.org_id || null,

      // Onboarding
      stepLibrary,
      onboardingTemplate,
      organization,
    }}>
      {dataSource === 'error' && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, padding: '8px 16px', background: '#ef4444', color: '#fff', fontSize: 12, fontFamily: 'monospace', textAlign: 'center', zIndex: 9999 }}>
          Unable to load data. Check your connection and <button onClick={loadCoreData} style={{ color: '#fff', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'monospace', fontSize: 12 }}>try again</button>.
        </div>
      )}
      {children}
    </DataContext.Provider>
  );
}

export const useData = () => useContext(DataContext);
